import { ResponsiveValue } from '@chakra-ui/styled-system';
import { Property } from 'csstype';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAppDispatch } from 'app/store';
import { setSidebarSettings } from 'app/store/Settings/slice';
import {
  ResizableBlockKey,
  getCursor,
  getMinWidth,
  getNewWidth,
  getWidth,
  isSmallScreen,
  showFullSideBarHeader,
  updateSiteSettings,
} from './utils';
import { SIDEBAR_KEY } from './constants';
import { useWindowSize } from '../userInterface';

export const useResizableBlock = (
  key: ResizableBlockKey = SIDEBAR_KEY,
  resizeDirection: 'ltr' | 'rtl' = 'ltr',
  useCompact: boolean = true
) => {
  const dispatch = useAppDispatch();
  const { width: screenWidth } = useWindowSize(50);
  const [isDragging, setIsDragging] = useState(false);
  const [isCompact, setIsCompact] = useState(false);
  const [forceCompactMode, setForceCompactMode] = useState(
    useCompact ? isSmallScreen(screenWidth) : false
  );

  const panelRef = useRef<HTMLDivElement>(null);
  const mousePosition = useRef<number>(0);

  const [cursor, setCursor] = useState<ResponsiveValue<Property.Cursor>>(
    getCursor(key, getWidth(key))
  );
  const [showFullHeader, setShowFullHeader] = useState(
    showFullSideBarHeader(key, getWidth(key))
  );

  const resize = useCallback(
    ({
      width,
      dx = 0,
      forceTransition = false,
      forceCompactMode = false,
    }: {
      width: string;
      dx?: number;
      forceTransition?: boolean;
      forceCompactMode?: boolean;
    }) => {
      if (panelRef.current) {
        const newWidth = getNewWidth(width, dx);
        panelRef.current.style.width = newWidth;

        panelRef.current.style.transition =
          forceTransition || parseInt(newWidth, 10) <= 180
            ? 'width 200ms'
            : 'none';

        setCursor(getCursor(key, newWidth));
        if (useCompact) {
          setIsCompact(forceCompactMode ? true : newWidth === getMinWidth(key));
        }
        setShowFullHeader(
          forceCompactMode ? false : showFullSideBarHeader(key, newWidth)
        );

        if (!forceCompactMode) {
          dispatch(
            setSidebarSettings({
              width: newWidth,
            })
          );
          updateSiteSettings(key, {
            width: newWidth,
          });
        }
      }
    },
    [dispatch, key, useCompact]
  );

  useEffect(() => {
    const forceCompactMode = useCompact ? isSmallScreen(screenWidth) : false;
    const width = forceCompactMode ? getMinWidth(key) : getWidth(key);
    if (useCompact) {
      setForceCompactMode(forceCompactMode);
    }
    resize({
      width,
      forceCompactMode,
      forceTransition: true,
    });
  }, [key, resize, screenWidth, useCompact]);

  const toggleMinMax = () => {
    if (panelRef.current) {
      const { width } = getComputedStyle(panelRef.current);
      const widthNumeric = parseInt(width, 10);
      const min = parseInt(getMinWidth(key), 10);

      return widthNumeric > min ? 'max' : 'min';
    }
    return null;
  };

  const handleMouseMove = (e: MouseEvent) => {
    const dx =
      resizeDirection === 'ltr'
        ? mousePosition.current - e.x
        : e.x - mousePosition.current;
    mousePosition.current = e.x;
    if (panelRef.current && dx !== 0) {
      const { width } = getComputedStyle(panelRef.current);
      resize({
        width,
        dx,
      });
    }
  };

  const handleMouseDown: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (forceCompactMode) {
      return;
    }

    e.preventDefault();
    setIsDragging(true);
    mousePosition.current = e.clientX;
    document.addEventListener('mousemove', handleMouseMove, false);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    document.removeEventListener('mousemove', handleMouseMove, false);
  };

  document.addEventListener('mouseup', handleMouseUp, false);

  return {
    handleMouseDown,
    cursor,
    resize,
    showFullHeader,
    toggleMinMax,
    panelRef,
    isDragging,
    forceCompactMode,
    isCompact,
  };
};
